import { inject, Injectable } from '@angular/core';
import {
  ChannelNotification,
  NotificationRule,
  NotificationSlugEnum,
} from '@app/generated/graphql';
import {
  INotificationConfig,
  INotificationMessageConfig,
  INotificationPreferenceConfig,
} from '../interfaces/interface';
import { Router } from '@angular/router';
import { EXAMPLE_NOTIFICATION_CONFIG } from '../configs/example.notification-config';
import { createOfferNotificationConfig } from '../configs/offer.notification-config';
import { createClientChangeRequestNotificationConfig } from '../configs/client-change-request.notification-config';
import { createOrderCommentNotificationConfig } from '../configs/order-comment.notification-config';
import { createPalletDataChangeNotificationConfig } from '../configs/pallet-data-change.notification-config';

@Injectable({
  providedIn: 'root',
})
export class NotificationPreferencesConfigService {
  // dependencies
  private _router = inject(Router);

  private readonly _NOTIFICATION_CONFIG: Partial<
    Record<NotificationSlugEnum, INotificationConfig>
  > = {
    [NotificationSlugEnum.Example]: EXAMPLE_NOTIFICATION_CONFIG,
    [NotificationSlugEnum.Offer]: createOfferNotificationConfig(this._router),
    [NotificationSlugEnum.ClientChangeRequest]:
      createClientChangeRequestNotificationConfig(this._router),
    [NotificationSlugEnum.OrderComment]: createOrderCommentNotificationConfig(
      this._router,
    ),
    [NotificationSlugEnum.PalletDataChange]:
      createPalletDataChangeNotificationConfig(this._router),
  } as const;

  getNotificationConfig(slug: NotificationSlugEnum): INotificationConfig {
    return this._NOTIFICATION_CONFIG[slug];
  }

  getNotificationPreferenceConfig(
    slug: NotificationSlugEnum,
  ): INotificationPreferenceConfig {
    return this._NOTIFICATION_CONFIG[slug].preference;
  }

  getNotificationMessageConfig(
    slug: NotificationSlugEnum,
  ): INotificationMessageConfig {
    return this._NOTIFICATION_CONFIG[slug].message;
  }

  /**
   * Resolved title/body for any channel notification (toast, drawers).
   * Uses optional `resolveDisplayTitle` / `resolveDisplayDescription` on the message config when present.
   */
  getResolvedChannelNotificationCopy(notification: ChannelNotification): {
    title: string;
    body: string;
  } {
    const rule: NotificationRule | undefined = notification.notificationRule;
    const slug: NotificationSlugEnum | undefined = rule?.notificationSlug;
    const metadata: Record<string, unknown> =
      this._channelMetadataRecord(notification.channelMetadata) ?? {};
    const messageConfig: INotificationMessageConfig | undefined =
      slug != null ? this._NOTIFICATION_CONFIG[slug]?.message : undefined;

    const resolvedTitle: string | undefined =
      messageConfig?.resolveDisplayTitle?.(metadata);
    const resolvedBody: string | undefined =
      messageConfig?.resolveDisplayDescription?.(metadata);

    const title: string =
      resolvedTitle ??
      notification.title ??
      rule?.name ??
      messageConfig?.displayTitle ??
      '';

    const body: string =
      resolvedBody ??
      notification.body ??
      messageConfig?.displayDescription ??
      rule?.description ??
      '';

    return { title, body };
  }

  private _channelMetadataRecord(
    metadata: ChannelNotification['channelMetadata'],
  ): Record<string, unknown> | undefined {
    if (metadata && typeof metadata === 'object' && !Array.isArray(metadata)) {
      return metadata as Record<string, unknown>;
    }

    return undefined;
  }
}
